import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ChevronDown, Loader2, Mic2 } from "lucide-react";
import { musicClient } from "@/lib/musicApi";
import { TranslateToggle } from "@/components/common/TranslateToggle";

const TIME_RE = /^((?:\[\d{1,2}:\d{2}(?:\.\d{1,2})?\])+)(.*)$/;

// Parse LRC -> ordered lines. Timed lines carry a numeric `time`; if NO line has
// a timestamp the whole thing is static text (we never fake sync). Reuses the
// backend's timestamps as-is, only the text is translated.
function parseLrc(lrc) {
  const out = [];
  let hasTimed = false;
  for (const raw of (lrc || "").split("\n")) {
    const m = raw.match(TIME_RE);
    if (m) {
      const tags = m[1].match(/\[(\d{1,2}):(\d{2})(?:\.(\d{1,2}))?\]/g) || [];
      const text = m[2].trim();
      for (const tag of tags) {
        const mm = tag.match(/\[(\d{1,2}):(\d{2})(?:\.(\d{1,2}))?\]/);
        const time = parseInt(mm[1], 10) * 60 + parseInt(mm[2], 10) + (mm[3] ? parseInt(mm[3].padEnd(2, "0"), 10) / 100 : 0);
        hasTimed = true;
        if (text) out.push({ time, text });
      }
    } else if (raw.trim() && !/^\[[a-z]+:/i.test(raw)) {
      out.push({ time: null, text: raw.trim() });
    }
  }
  out.sort((a, b) => (a.time ?? 1e9) - (b.time ?? 1e9));
  return { lines: out, synced: hasTimed };
}

export const LyricsPanel = ({ track, progress, onSeek, onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const [state, setState] = useState("loading"); // loading | ok | none | error | gated
  const [lrc, setLrc] = useState("");
  const [enLrc, setEnLrc] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [translated, setTranslated] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const activeRef = useRef(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await musicClient.get(`/tracks/${track.id}/lyrics`, { params: { lang } });
      if (!data.available) { setState("none"); return; }
      setLrc(data.lrc || "");
      setTranslated(!!data.translated);
      setShowOriginal(false);
      setState("ok");
    } catch (e) {
      if (e?.response?.status === 403) setState("gated");
      else setState("error");
    }
  }, [track.id, lang]);

  useEffect(() => { load(); }, [load]);

  const onToggle = useCallback(async () => {
    if (lang === "en" || !translated) return;
    if (enLrc == null && !showOriginal) {
      setToggleBusy(true);
      try {
        const { data } = await musicClient.get(`/tracks/${track.id}/lyrics`, { params: { lang: "en" } });
        setEnLrc(data.lrc || "");
      } catch { /* keep translated */ } finally { setToggleBusy(false); }
    }
    setShowOriginal((v) => !v);
  }, [lang, translated, enLrc, showOriginal, track.id]);

  const shown = showOriginal && enLrc != null ? enLrc : lrc;
  const { lines, synced } = useMemo(() => parseLrc(shown), [shown]);

  const activeIdx = useMemo(() => {
    if (!synced) return -1;
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].time != null && lines[i].time <= (progress || 0) + 0.15) idx = i; else if (lines[i].time != null) break;
    }
    return idx;
  }, [lines, synced, progress]);

  useEffect(() => {
    if (activeRef.current) activeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeIdx]);

  return (
    <motion.div
      initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
      transition={{ type: "spring", stiffness: 260, damping: 30 }}
      data-testid="lyrics-panel"
      className="fixed inset-0 z-[10001] flex flex-col"
      style={{ background: "var(--bg-primary)" }}
    >
      <div className="flex items-center justify-between px-6 py-5 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Mic2 size={18} style={{ color: "var(--accent-primary)" }} />
          <div className="min-w-0">
            <p className="text-sm font-bold truncate" style={{ color: "var(--text-primary)" }}>{t("lyrics.title")}</p>
            <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{track.title}</p>
          </div>
        </div>
        <button onClick={onClose} data-testid="lyrics-close" aria-label={t("lyrics.closeAria")} style={{ color: "var(--text-primary)" }}><ChevronDown size={26} /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-32">
        {state === "loading" && (
          <div className="flex flex-col items-center justify-center h-full gap-3" data-testid="lyrics-loading">
            <Loader2 className="animate-spin" size={26} style={{ color: "var(--accent-primary)" }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("lyrics.loading")}</p>
          </div>
        )}
        {state === "none" && <p className="text-center text-sm py-24" data-testid="lyrics-none" style={{ color: "var(--text-secondary)" }}>{t("lyrics.none")}</p>}
        {state === "gated" && <p className="text-center text-sm py-24" data-testid="lyrics-gated" style={{ color: "var(--text-secondary)" }}>{t("members.body")}</p>}
        {state === "error" && (
          <button onClick={load} data-testid="lyrics-error" className="w-full text-center text-sm py-24" style={{ color: "var(--text-secondary)" }}>{t("lyrics.error")}</button>
        )}
        {state === "ok" && (
          <div className="max-w-xl mx-auto pt-6">
            {showOriginal === false && lang !== "en" && !translated && (
              <p className="text-xs mb-4 text-center" data-testid="lyrics-original-note" style={{ color: "var(--text-secondary)" }}>{t("lyrics.originalNote")}</p>
            )}
            {lines.map((ln, i) => (
              <p
                key={i}
                ref={i === activeIdx ? activeRef : null}
                data-testid={`lyrics-line-${i}`}
                onClick={() => { if (synced && ln.time != null) onSeek(ln.time); }}
                className={`py-1.5 text-xl font-bold leading-snug transition-opacity ${synced && ln.time != null ? "cursor-pointer" : ""}`}
                style={{
                  color: i === activeIdx ? "var(--accent-primary)" : "var(--text-primary)",
                  opacity: !synced ? 1 : (i === activeIdx ? 1 : 0.4),
                }}
              >
                {ln.text}
              </p>
            ))}
          </div>
        )}
      </div>

      {state === "ok" && lang !== "en" && translated && (
        <div className="absolute bottom-8 left-0 right-0 flex justify-center">
          <TranslateToggle
            testid="lyrics-translate-toggle"
            translating={toggleBusy}
            hasTranslation={translated}
            showOriginal={showOriginal}
            onToggle={onToggle}
            className="px-4 py-2 rounded-full"
            style={{ background: "var(--bg-subtle)", color: "var(--accent-primary)" }}
            size={14}
          />
        </div>
      )}
    </motion.div>
  );
};

export default LyricsPanel;
